import { useCallback } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';
import { Select, MenuItem } from '@mui/material';
import { ProductReadModel } from '@client/api';
import { ProductsPaths } from '../../../../products';
import { useUserPermission } from '../../../../../hooks/useUserPermission/useUserPermission';
import { PermissionKeys } from '@client/api';

interface AgreementSpaceProductSelectProps {
  value: string;
  products: ProductReadModel[];
  onChange: (productId: string) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  className?: string;
}

export function AgreementSpaceProductSelect({
  value,
  products,
  onChange,
  isLoading = false,
  readOnly = false,
  className,
}: AgreementSpaceProductSelectProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const navigate = useNavigate();
  const { hasPermission } = useUserPermission();

  const handleChange = (event: { target: { value: unknown } }) => {
    const selectedProductId = event.target.value as string;
    if (selectedProductId) {
      onChange(selectedProductId);
    }
  };

  const handleCreateProduct = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(generatePath(ProductsPaths.ProductCreate));
  }, [navigate]);

  const renderValue = (selected: unknown) => {
    if (!selected) {
      return <span className="opacity-50">– {tGlobal('agreementSpace.selectProduct')} –</span>;
    }
    const product = products.find((p) => p.id === selected);
    if (!product) return '';
    return product.name;
  };

  const isDisabled = isLoading || readOnly;

  return (
    <Select
      size="small"
      className={classNames('w-full !text-primary border border-border', isDisabled && 'cursor-not-allowed', className)}
      value={value}
      onChange={handleChange}
      renderValue={renderValue}
      displayEmpty
      disabled={isDisabled}
      sx={{
        fontSize: '12px',
        '&.Mui-disabled': {
          cursor: 'not-allowed',
          pointerEvents: 'auto',
          opacity: 1,
          color: 'inherit',
          '-webkit-text-fill-color': 'inherit',
          '& .MuiSelect-select': {
            cursor: 'not-allowed',
            '-webkit-text-fill-color': 'inherit',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            cursor: 'not-allowed',
            borderColor: 'rgb(38 38 38)', // neutral-800
          },
          '& .MuiSvgIcon-root': {
            cursor: 'not-allowed',
            color: 'inherit',
          },
        },
      }}
    >
      <MenuItem
        key="create-product"
        value=""
        onClick={handleCreateProduct}
        disabled={!hasPermission([PermissionKeys.Tenant.ProductCreate])}
      >
        {tCommon('addEntity', { entity: tGlobal('entity.product').toLowerCase() })}
      </MenuItem>
      {products.map((product) => (
        <MenuItem key={product.id} value={product.id}>
          {product.name}
        </MenuItem>
      ))}
    </Select>
  );
}
